import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const timelineApi = createApi({
  reducerPath: "timelineApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Timeline"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Timeline items
    getTimeline: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "timeline_date",
        order = "desc",
      } = {}) => ({
        url: "/timeline",
        params: { page, limit, search, sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        timelineData: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.timelineData?.length
          ? [
              ...result.timelineData.map(({ id }) => ({
                type: "Timeline",
                id: Number(id),
              })),
              { type: "Timeline", id: "LIST" },
            ]
          : [{ type: "Timeline", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get Timeline By ID
    getTimelineById: builder.query({
      query: (id) => `/timeline/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Timeline", id }],
    }),

    // Create Timeline item
    createTimeline: builder.mutation({
      query: (data) => ({
        url: "/timeline",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Timeline", id: "LIST" }],
    }),

    // Update Timeline item
    updateTimeline: builder.mutation({
      query: ({ id, data }) => ({
        url: `/timeline/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Timeline", id },
        { type: "Timeline", id: "LIST" },
      ],
    }),

    // Delete Timeline item
    deleteTimeline: builder.mutation({
      query: (id) => ({
        url: `/timeline/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Timeline", id: "LIST" }],
    }),

    /* Toggle Timeline Status */
    toggleTimelineStatus: builder.mutation({
      query: (id) => ({
        url: `/timeline/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Timeline", id },
        { type: "Timeline", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetTimelineQuery,
  useGetTimelineByIdQuery,
  useCreateTimelineMutation,
  useUpdateTimelineMutation,
  useDeleteTimelineMutation,
  useToggleTimelineStatusMutation,
} = timelineApi;
